import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../helpers/axiosInstance';
import Swal from 'sweetalert2';
import View from '../images/view.svg';
import Edit from '../images/edit.png';
import Delete from "../images/delete.svg";
import { FaFilter } from 'react-icons/fa';
import { IoClose } from "react-icons/io5";
import { useLoading } from '../context/LoadingContext';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import * as XLSX from 'xlsx';

const Shop = () => {
    const [shopName, setShopName] = useState('');
    const [businessUnit, setBusinessUnit] = useState('');
    const [businessUnits, setBusinessUnits] = useState([]);
    const [shops, setShops] = useState([]);
    const [allFilteredShops, setAllFilteredShops] = useState([]);
    const [page, setPage] = useState(1);
    const [inputPage, setInputPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [showStatusFilter, setShowStatusFilter] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [statusOptions, setStatusOptions] = useState([]);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedShop, setSelectedShop] = useState(null);
    const [formData, setFormData] = useState({
        shop_name: "",
        business_unit_id: ""
    });
    const { updateLoading, setUpdateLoading } = useLoading();
    const debouncedSearchRef = useRef();
    const navigate = useNavigate();
    useEffect(() => {
        axiosInstance.get('/api/businessunit/business-units')
            .then(response => {
                setBusinessUnits(response.data);
                const unitNames = response.data.map(unit => unit.unit_name);
                setStatusOptions(unitNames);
            })
            .catch(error => console.error("Error fetching business units:", error));
    }, [updateLoading]);

    const fetchShops = useCallback(async () => {
        updateLoading(true);

        try {
            const response = await axiosInstance.get("/api/shop/shops", {
                params: {
                    limit: 1000,
                    search: searchTerm,
                },
            });

            const allShops = response.data.shops;

            const filtered = allShops.filter(shop =>
                selectedStatus
                    ? shop.BusinessUnit?.unit_name.toLowerCase() === selectedStatus.toLowerCase()
                    : true
            );

            setAllFilteredShops(filtered);

            const totalFilteredPages = Math.ceil(filtered.length / 10);
            let startIdx = (page - 1) * 10;
            let endIdx = startIdx + 10;
            let matchedShops = filtered.slice(startIdx, endIdx);

            if (matchedShops.length === 0 && totalFilteredPages > 0) {
                let found = false;

                for (let i = 1; i <= totalFilteredPages; i++) {
                    if (i === page) continue;

                    const altStart = (i - 1) * 10;
                    const altEnd = altStart + 10;
                    const altSlice = filtered.slice(altStart, altEnd);

                    if (altSlice.length > 0) {
                        matchedShops = altSlice;
                        setPage(i);
                        setInputPage(i);
                        found = true;
                        break;
                    }
                }

                if (!found) {
                    setShops([]);
                    setTotalPages(0);
                    if (page !== 1) {
                        setPage(1);
                        setInputPage(1);
                    }
                    updateLoading(false);
                    return;
                }
            }

            setShops(matchedShops);
            setTotalPages(totalFilteredPages);
            setInputPage(page);

        } catch (error) {
            console.error("Error fetching shops:", error);
            setShops([]);
            setTotalPages(0);
            setPage(1);
            setInputPage(1);
            setAllFilteredShops([]);
        }

        updateLoading(false);
    }, [page, searchTerm, selectedStatus, updateLoading]);

    useEffect(() => {
        debouncedSearchRef.current = debounce((value) => {
            setPage(1);
        }, 500);

        return () => {
            debouncedSearchRef.current.cancel();
        };
    }, []);

    useEffect(() => {
        if (debouncedSearchRef.current) {
            debouncedSearchRef.current(searchTerm);
        }
    }, [searchTerm]);

    useEffect(() => {
        fetchShops();
    }, [page, selectedStatus, fetchShops]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
            setInputPage(newPage);
        }
    };

    const handleInputChange = (e) => {
        setInputPage(e.target.value);
    };

    const handleInputSubmit = () => {
        const newPage = Number(inputPage);
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        } else {
            setInputPage(page); // reset invalid input
        }
    };

    const handleView = (id) => {
        navigate(`/shop/${id}`);
    };

    const exportToExcel = useCallback(() => {
        try {
            const data = allFilteredShops.map((shop, index) => ({
                'Sl. No.': index + 1,
                'Shop Name': shop.shop_name,
                'Business Unit': shop.BusinessUnit ? shop.BusinessUnit.unit_name : "N/A"
            }));

            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Shops");

            // SweetAlert popup before download
            Swal.fire({
                icon: "success",
                title: "Export Started",
                text: "File Downloading has Started!",
                confirmButtonText: "OK",
            });

            // Trigger file download
            XLSX.writeFile(wb, `shops_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (error) {
            console.error("Error exporting shops:", error);
            Swal.fire("Error!", "Failed to export file. Please try again.", "error");
        }
    }, [allFilteredShops]);


    const handleAdd = async () => {
        if (!shopName || !businessUnit) {
            Swal.fire("Error", "Please fill all fields", "error");
            return;
        }

        const requestData = {
            shop_name: shopName,
            business_unit_id: Number(businessUnit)
        };

        updateLoading(true);
        try {
            await axiosInstance.post("/api/shop/shop", requestData);
            Swal.fire("Success", "Shop added successfully!", "success");
            setShopName('');
            setBusinessUnit('');
            setShowAddForm(false);
            fetchShops();
        } catch (error) {
            console.error("Error creating shop:", error.response?.data || error.message);
            Swal.fire("Error", error.response?.data?.error || "Failed to add shop", "error");
        } finally {
            updateLoading(false);
        }
    };

    const handleCancel = () => {
        setShopName('');
        setBusinessUnit('');
        setShowAddForm(false);
    };

    // const handleEditClick = (shop) => {
    //     setSelectedShop(shop);
    //     setFormData({
    //         shop_name: shop.shop_name,
    //         business_unit_id: shop.business_unit_id
    //     });
    //     setEditModalOpen(true);
    // };

    const handleUpdateShop = () => {
        axiosInstance
            .put(`/api/shop/shops/${selectedShop.id}`, {
                shop_name: formData.shop_name,
                business_unit_id: formData.business_unit_id,
            })
            .then((res) => {
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: res.data.message || "Shop updated successfully",
                });

                setEditModalOpen(false);
                fetchShops();
            })
            .catch((err) => {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: err.response?.data?.message || "Failed to update shop",
                });
                console.error("Update error:", err);
            });
    };




    return (
        <div className="container mt-5">
            <div className="d-flex justify-content-between align-items-center mb-4 me-4 ms-4">
                <input
                    type="text"
                    className="form-control w-25 rounded-pill"
                    placeholder="Search by shop name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div className="d-flex align-items-center gap-2">
                    <button className="btn" onClick={() => setShowAddForm(!showAddForm)} style={{ backgroundColor: "rgb(10, 69, 149)", color: "white" }}>
                        {showAddForm ? "Hide Form" : "Add Shop"}
                    </button>
                    <button className="btn" onClick={exportToExcel} style={{ backgroundColor: "rgb(10, 69, 149)", color: "white" }}>
                        Export Data
                    </button>
                </div>
            </div>

            {showAddForm && (
                <div className="card p-4 shadow-lg border-0 rounded-4 mb-4">
                    <h4 className="fw-bold text-center" style={{ color: "rgb(10, 69, 149)" }}>Add Shop</h4>
                    <div className="row g-4 mt-3">
                        <div className="col-md-6">
                            <label className='form-label fw-bold'>Business Unit</label>
                            <select
                                className="form-select rounded-pill"
                                value={businessUnit}
                                onChange={(e) => setBusinessUnit(e.target.value)}
                            >
                                <option value="" disabled>Select Business Unit</option>
                                {businessUnits.map(unit => (
                                    <option key={unit.id} value={unit.id}>{unit.unit_name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className='form-label fw-bold'>Shop Name</label>
                            <input
                                type="text"
                                className="form-control rounded-pill"
                                placeholder="Enter Name"
                                value={shopName}
                                onChange={(e) => setShopName(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="d-flex justify-content-center mt-4 gap-3">
                        <button className="btn" onClick={handleAdd} style={{ backgroundColor: "rgb(10, 69, 149)", color: "white" }}>Add</button>
                        <button className="btn btn-secondary rounded-pill px-4" onClick={handleCancel}>Cancel</button>
                    </div>
                </div>
            )}

            <div className="table-responsive mx-4 mt-4" style={{ maxHeight: "600px", overflowY: "auto" }}>
                <table className="table table-sm">
                    <thead className="text-white" style={{ position: "sticky", top: 0, zIndex: 10 }}>
                        <tr>
                            <th className="text-center text-white py-2 px-0" style={{ backgroundColor: "#0A4595", minWidth: "80px" }}>Sl. No.</th>
                            <th className="text-center text-white py-2 px-0" style={{ backgroundColor: "#0A4595", minWidth: "200px" }}>Shop Name</th>
                            <th className="text-center text-white py-2 px-0" style={{ backgroundColor: "#0A4595", minWidth: "200px" }}>Business Unit
                                <FaFilter className="ms-2 cursor-pointer" onClick={() => setShowStatusFilter(!showStatusFilter)} />
                                <br />
                                {showStatusFilter && (
                                    <div
                                        className="dropdown-menu show p-2 shadow rounded"
                                        style={{ backgroundColor: "#f8f9fa", zIndex: 20, minWidth: "150px", position: "fixed" }}
                                    >
                                        {statusOptions.length > 0 ? (
                                            statusOptions.map((status) => (
                                                <div
                                                    key={status}
                                                    className="dropdown-item rounded px-3 py-2 text-dark fw-semibold hover-bg"
                                                    onClick={() => {
                                                        setSelectedStatus(status.toLowerCase());
                                                        setShowStatusFilter(false);
                                                    }}
                                                    style={{
                                                        cursor: "pointer",
                                                        transition: "background-color 0.2s",
                                                    }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e9ecef")}
                                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                                                >
                                                    {status}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-muted">No Business Units Found</div>
                                        )}
                                    </div>
                                )}

                                {selectedStatus && (
                                    <div className="bg-light d-inline-flex align-items-center px-1 py-0 rounded mt-1" style={{ fontSize: "12px", marginLeft: "5px", color: 'black' }}>
                                        {selectedStatus}
                                        <IoClose className="text-danger cursor-pointer ms-1" style={{ fontSize: "14px" }} onClick={() => setSelectedStatus("")} />
                                    </div>
                                )}
                            </th>
                            <th className="text-center text-white py-2 px-0" style={{ backgroundColor: "#0A4595", minWidth: "150px" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shops.length > 0 ? (
                            shops
                                .filter(shop =>
                                    selectedStatus
                                        ? shop.BusinessUnit?.unit_name.toLowerCase() === selectedStatus.toLowerCase()
                                        : true
                                )
                                .map((shop, index) => (
                                    <tr key={shop.id}>
                                        <td className="text-center p-2">{(page - 1) * 10 + index + 1}</td>
                                        <td className="text-center p-2">{shop.shop_name}</td>
                                        <td className="text-center p-2">{shop.BusinessUnit ? shop.BusinessUnit.unit_name : "N/A"}</td>
                                        <td className="text-center p-2">
                                            <img src={View} alt="View" className="action-icon mx-2" style={{ width: "18px", height: "18px", cursor: 'pointer' }} title="View" onClick={() => handleView(shop.id)} />
                                            <img
                                                src={Edit}
                                                alt="Edit"
                                                className="action-icon mx-2"
                                                style={{ width: "18px", height: "18px", cursor: "pointer" }}
                                                title="Edit"
                                                onClick={() => {
                                                    setSelectedShop(shop); // pass the shop object
                                                    setFormData({
                                                        shop_name: shop.shop_name,
                                                        business_unit_id: shop.business_unit_id,
                                                    });
                                                    setEditModalOpen(true);
                                                }}
                                            />
                                            <img src={Delete} alt="Delete" className="action-icon mx-2" style={{ width: "18px", height: "18px", cursor: "pointer" }} title="Delete" />
                                        </td>
                                    </tr>
                                ))
                        ) : (
                            <tr>
                                <td colSpan="4" className="text-center p-3">No shops available</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {editModalOpen && (
                <div
                    onClick={() => setEditModalOpen(false)}
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0, 0, 0, 0.1)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999,
                        padding: "20px",
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "#fff",
                            borderRadius: "16px",
                            width: "100%",
                            maxWidth: "600px",
                            padding: "24px",
                            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.0)",
                            overflowY: "auto",
                            maxHeight: "90vh",
                            fontFamily: "'Inter', sans-serif",
                        }}
                    >
                        {/* Header */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                borderBottom: "1px solid #eee",
                                paddingBottom: "16px",
                                marginBottom: "24px",
                            }}
                        >
                            <h2 style={{ margin: 0, fontSize: "22px", fontWeight: 600, color: "#333" }}>
                                Edit Shop
                            </h2>
                            <button
                                onClick={() => setEditModalOpen(false)}
                                style={{
                                    background: "none",
                                    border: "none",
                                    fontSize: "22px",
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                    color: "#999",
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Form Inputs in One Row */}
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleUpdateShop();
                            }}
                            style={{
                                display: "flex",
                                gap: "20px",
                                flexWrap: "wrap",
                                marginBottom: "20px",
                            }}
                        >
                            <div style={{ flex: 1, minWidth: "200px" }}>
                                <label
                                    style={{
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        marginBottom: "6px",
                                        display: "block",
                                        color: "#333",
                                    }}
                                >
                                    Shop Name
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.shop_name}
                                    onChange={(e) =>
                                        setFormData({ ...formData, shop_name: e.target.value })
                                    }
                                    style={{
                                        padding: "10px",
                                        borderRadius: "8px",
                                        border: "1px solid #ccc",
                                        fontSize: "13px",
                                        width: "100%",
                                    }}
                                />
                            </div>

                            <div style={{ flex: 1, minWidth: "200px" }}>
                                <label
                                    style={{
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        marginBottom: "6px",
                                        display: "block",
                                        color: "#333",
                                    }}
                                >
                                    Business Unit
                                </label>
                                <select
                                    className="form-control"
                                    value={formData.business_unit_id}
                                    onChange={(e) =>
                                        setFormData({ ...formData, business_unit_id: e.target.value })
                                    }
                                    style={{
                                        padding: "10px",
                                        borderRadius: "8px",
                                        border: "1px solid #ccc",
                                        fontSize: "13px",
                                        width: "100%",
                                    }}
                                >
                                    <option value="">Select Unit</option>
                                    {businessUnits.map((unit) => (
                                        <option key={unit.id} value={unit.id}>
                                            {unit.unit_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </form>

                        {/* Buttons */}
                        <div style={{ textAlign: "right" }}>
                            <button
                                type="button"
                                onClick={() => setEditModalOpen(false)}
                                style={{
                                    padding: "10px 16px",
                                    marginRight: "10px",
                                    backgroundColor: "#e0e0e0",
                                    color: "#333",
                                    border: "none",
                                    borderRadius: "6px",
                                    fontSize: "13px",
                                    cursor: "pointer",
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleUpdateShop}
                                style={{
                                    padding: "10px 20px",
                                    backgroundColor: 'rgb(10, 69, 149)',
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "6px",
                                    fontSize: "13px",
                                    cursor: "pointer",
                                }}
                            >
                                Update
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="row justify-content-center mt-4 mb-5">
                <div className="col-auto">
                    <button
                        className="btn btn-sm mx-2"
                        onClick={() => handlePageChange(page - 1)}
                        style={{ backgroundColor: "rgb(10, 69, 149)", color: "white" }}
                        disabled={page === 1}
                    >
                        Previous
                    </button>
                </div>

                <div className="col-auto d-flex align-items-center">
                    <span className="mx-2">Page</span>
                    <input
                        type="number"
                        value={shops.length === 0 ? 0 : inputPage}
                        onChange={handleInputChange}
                        onBlur={handleInputSubmit}
                        onKeyDown={(e) => e.key === "Enter" && handleInputSubmit()}
                        style={{ width: "60px", textAlign: "center" }}
                        min={1}
                        max={totalPages}
                        disabled={shops.length === 0}
                    />
                    <span className="mx-2">of {totalPages}</span>
                </div>

                <div className="col-auto">
                    <button
                        className="btn btn-sm mx-2"
                        onClick={() => handlePageChange(page + 1)}
                        style={{ backgroundColor: "rgb(10, 69, 149)", color: "white" }}
                        disabled={page === totalPages}
                    >
                        Next
                    </button>
                </div>
            </div>

        </div>

    );
};

export default Shop;